import { Button, Input, Textarea } from '@admin-panel/ui';
import { updateAttributesOfCdnImage } from '@admin-panel/utils';
import { PlusIcon, TrashIcon, XIcon } from '@phosphor-icons/react';

import { ProductDepthRail, useProductDepthByStyleNumbers } from './productDepth';
import { SectionHeading } from './helpers';
import type { ProductRecommendationItem } from './types';

interface ProductRecommendationsSectionProps {
  showHeading?: boolean;
  presentation?: 'edit' | 'summary';
  actionLabel?: string;
  actionDisabled?: boolean;
  actionLoading?: boolean;
  onAction?: () => void;
  recommendations: Array<ProductRecommendationItem>;
  onUpdateRecommendation: (
    recommendationIndex: number,
    updater: (current: ProductRecommendationItem) => ProductRecommendationItem,
  ) => void;
  onAddRecommendation: () => void;
  onRemoveRecommendation: (recommendationIndex: number) => void;
  onRemoveMappedProduct?: (recommendationIndex: number, productId: string) => void;
  onOpenProductPicker: (
    recommendationIndex: number,
    productType: string,
    mode?: 'recommendations' | 'selection',
  ) => void;
}

export const ProductRecommendationsSection = ({
  showHeading = true,
  presentation = 'edit',
  actionLabel,
  actionDisabled = false,
  actionLoading = false,
  onAction,
  recommendations,
  onUpdateRecommendation,
  onAddRecommendation,
  onRemoveRecommendation,
  onRemoveMappedProduct,
  onOpenProductPicker,
}: ProductRecommendationsSectionProps) => {
  const isSummary = presentation === 'summary';
  const selectedStyleNumbers = Array.from(
    new Set(
      recommendations
        .flatMap(recommendation => [
          ...(recommendation?.mapped_lk_styles || []),
          ...Object.keys(recommendation?.mapped_product_images || {}),
        ])
        .map(value => String(value))
        .filter(Boolean),
    ),
  );
  const depthByStyleNumber = useProductDepthByStyleNumbers(selectedStyleNumbers);

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="space-y-1">
          {showHeading ? <SectionHeading title="Product Recommendations" /> : <span />}
        </div>
        {isSummary ? (
          actionLabel && onAction ? (
            <Button
              type="button"
              variant="outline"
              size="small"
              isDisabled={actionDisabled}
              isLoading={actionLoading}
              onClick={onAction}
            >
              {actionLabel}
            </Button>
          ) : null
        ) : (
          <Button
            type="button"
            variant="outline"
            size="small"
            icon={<PlusIcon size={14} />}
            onClick={onAddRecommendation}
          >
            Add Recommendation
          </Button>
        )}
      </div>

      {recommendations.length === 0 ? (
        <div className="space-y-2 rounded-md border border-dashed border-border/70 p-3">
          <p className="text-sm text-muted-foreground">No recommendations available.</p>
          <Button
            type="button"
            variant="outline"
            size="small"
            icon={<PlusIcon size={14} />}
            onClick={onAddRecommendation}
          >
            Add First Recommendation
          </Button>
        </div>
      ) : (
        recommendations.map((recommendation, recommendationIndex) => {
          const mappedIds = Array.from(
            new Set((recommendation?.mapped_lk_styles || []).map(value => String(value))),
          );
          const mappedImages = recommendation?.mapped_product_images || {};

          return (
            <article
              key={`${recommendation.product_type || 'recommendation'}-${recommendationIndex}`}
              className="space-y-2 rounded-md border border-border/70 p-2.5"
            >
              {isSummary ? (
                <div className="space-y-1">
                  <p className="m-0 text-sm font-medium capitalize text-foreground">
                    {(recommendation.product_type || 'Products').replace(/_/g, ' ')}
                  </p>
                  {recommendation.description ? (
                    <p className="m-0 text-sm leading-6 text-muted-foreground">
                      {recommendation.description}
                    </p>
                  ) : null}
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto] sm:items-end">
                  <div className="space-y-1">
                    <p className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                      Product Type
                    </p>
                    <Input
                      value={recommendation.product_type ?? ''}
                      placeholder="e.g. bags, footwear, jewellery"
                      onChange={event => {
                        const productType = event.target.value;
                        onUpdateRecommendation(recommendationIndex, current => ({
                          ...current,
                          product_type: productType,
                        }));
                      }}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    tone="destructive"
                    size="small"
                    icon={<TrashIcon size={14} />}
                    onClick={() => onRemoveRecommendation(recommendationIndex)}
                  >
                    Remove
                  </Button>
                </div>
              )}

              <div className="space-y-2">
                {mappedIds.length === 0 ? (
                  <p className="text-[11px] text-muted-foreground">No products added yet.</p>
                ) : null}

                <div className="flex flex-wrap items-center gap-2">
                  {mappedIds.map((productId, previewIndex) => {
                    const imageUrl =
                      typeof mappedImages[productId] === 'string'
                        ? updateAttributesOfCdnImage(mappedImages[productId] as string, {
                            width: 240,
                            quality: 100,
                          }) || (mappedImages[productId] as string)
                        : '';
                    const productDepth = depthByStyleNumber[productId];

                    return (
                      <div
                        key={`${recommendationIndex}-${productId}-${previewIndex}`}
                        className={[
                          'group relative',
                          isSummary ? 'aspect-2/3 h-36 rounded-sm' : 'h-10 w-10 rounded-md',
                        ].join(' ')}
                        title={productId}
                      >
                        <div
                          className={[
                            'relative h-full w-full overflow-hidden bg-muted/20',
                            isSummary
                              ? 'rounded-sm'
                              : 'rounded-md border border-border/80 bg-muted/30',
                          ].join(' ')}
                        >
                          {imageUrl ? (
                            <img
                              src={imageUrl}
                              alt={productId}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center px-1 text-[10px] font-medium text-muted-foreground">
                              {productId}
                            </div>
                          )}
                          {isSummary ? (
                            <ProductDepthRail
                              inventoryDepth={productDepth?.inventoryDepth}
                              jitDepth={productDepth?.jitDepth}
                            />
                          ) : null}
                        </div>

                        <Button
                          variant="outline"
                          color="neutral"
                          size="small"
                          className={[
                            'absolute -right-1 -top-1 z-10 h-6 min-h-0 w-6 min-w-0 rounded-full p-0 opacity-0 shadow-sm transition-opacity',
                            'group-hover:opacity-100',
                          ].join(' ')}
                          onClick={() => {
                            if (onRemoveMappedProduct) {
                              onRemoveMappedProduct(recommendationIndex, productId);
                              return;
                            }

                            onUpdateRecommendation(recommendationIndex, current => ({
                              ...current,
                              mapped_lk_styles: (current.mapped_lk_styles || []).filter(
                                value => String(value) !== productId,
                              ),
                              mapped_product_images: Object.fromEntries(
                                Object.entries(current.mapped_product_images || {}).filter(
                                  ([key]) => key !== productId,
                                ),
                              ),
                            }));
                          }}
                        >
                          <XIcon size={12} />
                        </Button>
                      </div>
                    );
                  })}
                  <Button
                    type="button"
                    variant="outline"
                    color="info"
                    onClick={() =>
                      onOpenProductPicker(
                        recommendationIndex,
                        recommendation.product_type || 'Products',
                        'selection',
                      )
                    }
                    className={[
                      'flex items-center justify-center rounded-sm border-dashed p-0 text-muted-foreground',
                      isSummary
                        ? 'aspect-2/3 h-36 min-h-0 w-auto min-w-0'
                        : 'h-10 min-h-0 w-10 min-w-0 rounded-md',
                    ].join(' ')}
                  >
                    <PlusIcon size={isSummary ? 20 : 14} />
                  </Button>
                </div>
                {!isSummary && mappedIds.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {mappedIds.map((productId, chipIndex) => (
                      <span
                        key={`${recommendationIndex}-chip-${productId}-${chipIndex}`}
                        className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground"
                      >
                        {productId}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>

              {!isSummary ? (
                <Textarea
                  placeholder="Product recommendation description"
                  rows={4}
                  className="min-h-[96px]"
                  value={recommendation.description ?? ''}
                  onChange={event => {
                    const description = event.target.value;
                    onUpdateRecommendation(recommendationIndex, current => ({
                      ...current,
                      description,
                    }));
                  }}
                />
              ) : null}
            </article>
          );
        })
      )}
    </section>
  );
};
